import { useEffect, useRef, useState } from "react";
import { invoke } from "@tauri-apps/api/core";
import { listen } from "@tauri-apps/api/event";
import { isTauri } from "@/lib/is-tauri";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";

export interface CursorControlState {
  requestId: string;
  executionId: string;
  phase: "confirm" | "active" | "moving" | "click" | "scroll" | "idle";
  action: string;
  x: number;
  y: number;
  displayId: number;
  revision: number;
  /** Whether this grant covers the user's real pointer.
   *
   *  False when the application is pressing a control through the accessibility
   *  layer: nothing moves on screen, so there is no cursor to place and nothing
   *  to consent to - the grant exists only so the run can be stopped. */
  pointer: boolean;
}

/**
 * Halt one execution's desktop control on this machine, without waiting for
 * the server: its in-flight pointer or typing action stops and it may not act
 * again. Failures are not reported: only a desktop window may ask.
 */
export async function releaseCursorControlForExecution(
  executionId: string,
): Promise<void> {
  if (!isTauri()) return;
  await invoke("cursor_control_stop_execution", { executionId }).catch(
    () => {},
  );
}

/** Native state is authoritative; subscribe before reading its snapshot. */
export function useCursorControl() {
  const [state, setState] = useState<CursorControlState | null>(null);
  const revision = useRef(-1);
  useEffect(() => {
    if (!isTauri()) return;
    let disposed = false;
    const apply = (next: CursorControlState | null) => {
      if (disposed || !next || next.revision <= revision.current) return;
      revision.current = next.revision;
      setState(next.phase === "idle" ? null : next);
    };
    const unlisten = listen<CursorControlState>(
      "denker://cursor-control",
      ({ payload }) => apply(payload),
    );
    void unlisten
      .then(() => {
        if (!disposed)
          return invoke<CursorControlState | null>("cursor_control_snapshot");
        return null;
      })
      .then(apply)
      .catch(() => {});
    return () => {
      disposed = true;
      void unlisten.then(safeTauriUnlisten).catch(() => {});
    };
  }, []);
  return state;
}
